import { useState, useEffect, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark } from '@fortawesome/free-solid-svg-icons';

import Dialog from '../components/Dialog';
import StoragePlusLoadingView from '../components/StoragePlusLoadingView';
import CornerGripperLogo from '../components/CornerGripperLogo';
import { HorizontalButtonPairView } from '../components/DRViewUtils';
import { joinCommaSeparatedNames } from './utils/composeText';
import { cancelColor, confirmColor, confirmBorderColor } from '../constants/Styles';
import { Locktube, LocktubeData } from '../constants/containerManagerInterfaces';
import { getConfig } from '../utils/configUtils';
import { r2h, countForReload } from '../vodjs/lib';
import { areSame, noteInteraction } from '../vodjs/Utils';
import RequestScreen from './RequestScreen';
import { ModalParams } from '../types/commonInterfaces';
import { Dropoff } from '../types/taskInterfaces';

import './BatchLoadItemsScreen.scss';
import { Task } from '../constants/taskInterfaces';
import LoadCancelScreen from './LoadCancelScreen';

enum Phase {
  Select = 'select',
  Scan = 'scan',
  Load = 'load',
  Ready = 'ready',
}

interface BatchLoadItemsScreenProps {
  clickCancel: () => void;
  clickConfirm: () => void;
  destinations: Destination[];
  originalDestinations: Destination[];
  setDestinations: (destinations: Destination[]) => void;
  storageState: LocktubeData; // TODO Mod to not need whole object
  toggleStorageSelection: (tubeId: string, destination: Destination | null) => void;
  requestedBy: string;
  tapToReload: () => void;
  hideDetails: boolean;
  isRestricted: boolean;
  phase: Phase;
  setPhase: (phase: Phase) => void;
  taskData: Task[];
  continueButtonText?: string;
  codeRequired?: boolean;
  autoPrioConfig?:any;
  getEnabledKioskFriendlyNames:() => String[];
  getOpenHoursInfo:(stationCode:String) => void;
  getDrawersLoadedForCurrentTask:() => String[];
  onClickCancelPendingUnload:() => void;
  taskUuid: string;
}

interface Destination {
  id: number;
  drawer: string;
  name: string;
  note: string;
  taskId: string;
}

interface Tubes {
  small: Locktube;
  medium: Locktube;
  large: Locktube;
}

export default function BatchLoadItemsScreen(props: BatchLoadItemsScreenProps) {
  const {
    clickCancel,
    clickConfirm,
    destinations,
    setDestinations,
    storageState,
    toggleStorageSelection,
    requestedBy,
    tapToReload,
    hideDetails,
    isRestricted,
    phase,
    setPhase,
    taskData,
    originalDestinations,
    continueButtonText,
    codeRequired,
    autoPrioConfig,
    getEnabledKioskFriendlyNames,
    getOpenHoursInfo,
    getDrawersLoadedForCurrentTask,
    onClickCancelPendingUnload,
    taskUuid
  } = props;

  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [confirmVisible, setConfirmVisible] = useState(false);
  const [showUnloadDialog, setShowUnloadDialog] = useState(false);
  const [onBotEditCtx, setOnBotEditCtx] = useState<any | {endGoals:String[], origBatch:Dropoff[], editedBatch:Dropoff[]}>(undefined);
  const supportNumber = getConfig().site?.supportNumber;
  const inputsRef = useRef<Array<HTMLDivElement>>([]);

  useEffect(() => {
    inputsRef.current = inputsRef.current.slice(0, destinations.length);
  }, [destinations]);

  /*
   * Handles updating the destinations state based on the locktube storageState
   */
  useEffect(() => {
    const getDrawer = (id: string) => {
      const tubes: Tubes = storageState.tubes;
      const tubeKeys = Object.keys(tubes);
      let drawer = '';

      tubeKeys.every((t: string) => {
        if (tubes[t as keyof Tubes].destination === id) {
          drawer = t;
          return false;
        }
        return true;
      });
      return drawer;
    };

    const updatedDestinations = destinations.map((d) => ({
      ...d,
      drawer: getDrawer(d.name),
    }));

    if (!areSame(destinations, updatedDestinations)) {
      setDestinations(updatedDestinations);
    }
  }, [storageState, destinations, setDestinations]);

  /*
   * Handles setting the load screen phase
   * select = user can assign drawers to dropoffs
   * scan = user needs to scan badge to open drawers
   * load = user is loading drawers
   * ready = drawers have been loaded and closed
   */
  useEffect(() => {
    const tubes = storageState.tubes;

    // Phase = load
    const allDrawersOpen = destinations.every((d) => {
      const tube = tubes[d.drawer as keyof Tubes];
      if (tube && tube.unlocked) {
        return true;
      }
      return false;
    });

    // Phase = ready
    const allDrawersFilled = destinations.every((d) => {
      const tube = tubes[d.drawer as keyof Tubes];
      if (tube && tube.accessStatus === 'filled' && tube.destination && tube.filled && !tube.unlocked) {
        return true;
      }
      return false;
    });

    // Phase = scan
    const allDrawersRequested = destinations.every((d) => {
      const tube = tubes[d.drawer as keyof Tubes];
      if (tube && (tube.accessStatus === 'requested' || tube.accessStatus === 'filled')) {
        return true;
      }
      return false;
    });

    if (allDrawersFilled) {
      setPhase(Phase.Ready);
    }
    else if (allDrawersOpen) {
      setPhase(Phase.Load);
    }
    else if (allDrawersRequested) {
      setPhase(Phase.Scan);
    }
    else {
      setPhase(Phase.Select);
    }
  }, [storageState.tubes, setPhase, destinations]);

  /*
   * Handles note changes (made outside of odweb)
   * TODO: make note handling better
   */
  useEffect(() => {
    const updatedDestinations = destinations.slice();
    let notesChanged = false;

    // Verify that the destination order has not changed (it should not, but...)
    const destinationsInOrder = () => {
      return updatedDestinations.every((d, index) => {
        if (d.id === originalDestinations[index].id) return true;
        return false;
      });
    };

    if (destinationsInOrder()) {
      originalDestinations.forEach((d) => {
        const task = taskData.find((t) => t.task_level_args.end_goal.value === d.name);

        if (task && d.note.trim() !== task.task_data.sender_note.trim()) {
          const idx = updatedDestinations.findIndex((ud) => ud.name === d.name);
          if (idx >= 0 && destinations[idx].note.trim() !== originalDestinations[idx].note.trim()) {
            updatedDestinations[idx].note = task.task_data.sender_note.trim();
            notesChanged = true;
          }
        }
      });

      if (notesChanged) {
        setDestinations(updatedDestinations);
      }
    }
  }, [taskData, originalDestinations, destinations, setDestinations]);


  const getDestSansDrawerIdx = () => {
    return destinations.findIndex((d) => !d.drawer);  //returns -1 if all dsts have drawers
  };


  const handleNoteChange = (id: number, value: string) => {
    const currDestinations = destinations.slice();
    const destintationIdx = currDestinations.findIndex((d) => d.id === id);
    if (destintationIdx >= 0) {
      currDestinations[destintationIdx].note = value;
    }
    setDestinations(currDestinations);
  };

  const handleDrawerToggle = (tubeId: string) => {
    const sansDrawerIdx = getDestSansDrawerIdx();
    if(sansDrawerIdx < 0) return;
    toggleStorageSelection(tubeId, destinations[sansDrawerIdx]);
  };

  const getTitle = () => {
    if (hideDetails) {
      return <span className="header__title-text">Load Moxi for Delivery</span>;
    }
    else {
      switch (phase) {
        case Phase.Select:
          let sansDrawerIdx = getDestSansDrawerIdx();
          let drawerName = sansDrawerIdx < 0 ? '' : r2h(destinations[sansDrawerIdx].name);

          return (
            <>
              <span className="header__title-text">Select a drawer to go to </span>
              <span className="header__title-text--emph">{drawerName}</span>
            </>
          );
        case Phase.Load:
          return <span className="header__title-text">Load the drawers, then close them</span>;
        case Phase.Ready:
          return (
            <>
              <span className="header__title-text">Ready to deliver to </span>
              <span className="header__title-text--emph">
                {joinCommaSeparatedNames(destinations.map((d) => r2h(d.name)))}
              </span>
            </>
          );
        case Phase.Scan:
          return <span className="header__title-text">Scan your badge on the sticker below this screen</span>;
      }
    }
  };

  const isDrawerOpen = (tubeId: string) => {
    return storageState.tubes[tubeId as keyof Tubes].unlocked;
  };

  const isDrawerReady = (tubeId: string) => {
    return (
      storageState.tubes[tubeId as keyof Tubes].filled &&
      storageState.tubes[tubeId as keyof Tubes].accessStatus === 'filled' &&
      !storageState.tubes[tubeId as keyof Tubes].unlocked
    );
  };

  const setDrawersRequested = () => {
    destinations.forEach((d) => {
      if (d.drawer) {
        toggleStorageSelection(d.drawer, d);
      }
    });
  };

  const isDrawerRequestedAndFilled = (tubeId: string) => {
    return (
      storageState.tubes[tubeId as keyof Tubes].filled &&
      storageState.tubes[tubeId as keyof Tubes].accessStatus === 'requested' &&
      !storageState.tubes[tubeId as keyof Tubes].unlocked
    );
  };

  const isOnetoTwoBatchLoaded = () => (drawersLoaded() || drawersRequestedAndFilled()) && destinations.length === 2;

  const isDeliverDisabled = () => {
    if (drawersRequested()) return true;
    if (isOnetoTwoBatchLoaded()) return false;
    return phase !== Phase.Ready;
  };

  const drawersLoaded = () => {
    return destinations.every((d) => {
      if (d.drawer) return isDrawerReady(d.drawer);
      return false;
    });
  };

  const drawersRequested = () => {
    return destinations.every((d) => {
      if (d.drawer) return storageState.tubes[d.drawer as keyof Tubes].accessStatus === 'requested';
      return false;
    });
  };

  const drawersRequestedAndFilled = () => {
    return destinations.every((d) => {
      if (d.drawer) return isDrawerRequestedAndFilled(d.drawer);
      return false;
    });
  };

  const clickDeliver = () => {
    if (destinations.length == 2 && isOnetoTwoBatchLoaded() && drawersRequestedAndFilled()) {
      setDrawersRequested();
    }
    clickConfirm();
  };

  const clickCancelTask = () => {
    if (destinations.length == 2 && isOnetoTwoBatchLoaded() && drawersRequestedAndFilled()) {
      setDrawersRequested();
    }
    clickCancel();
  };


  //"go back" means "unmark the highest-ordinal drawer's station"
  const showCancelRatherThanGoBack = () => {
    return (
      //no destinations have any drawers associated with them
      destinations.findIndex(d => d.drawer) < 0 ||

      //all drawer associations are for filled drawers
      destinations.findIndex(d => d.drawer && !storageState.tubes[d.drawer].filled) < 0 ||

      (
        (drawersLoaded() || drawersRequestedAndFilled()) && 
        phase !== Phase.Ready && 
        destinations.length == 2
      )
    );
  };

  const clickGoBack = () => {
    if (showCancelRatherThanGoBack()) {
      setShowCancelDialog(true);
    }
    else if (phase === Phase.Ready) {
      setDrawersRequested();
    }
    else if (phase === Phase.Select || phase === Phase.Scan) {
      // const lastRequested = destinations.slice().reverse().find((d) => !!d.drawer);
      //typescript doesn't recognize known/established function .findLastIndex (!)
      for(let i = destinations.length - 1; i >= 0; --i) {
        const drawer = destinations[i].drawer;
        if(drawer && !storageState.tubes[drawer].filled) {
          toggleStorageSelection(drawer, null);
          break;
        }
      }
    }
  };



  //Prohibit stop changes if any drawer is either unlocked or filled:
  // - If such state was caused by this batch load, we're past ammendability
  // - If it was an unrelated task gone bad leaving remnant state, we  
  //     choose not to burn dev cycles for that rogue case.
  const drawersAllowStopChanges = () =>  {
    return !Object.keys(storageState.tubes).some(d => {
      const tube = storageState.tubes[d];
      return tube.unlocked || tube.filled;
    });
  };

  //verify we have task data for all destinations
  const verifyDestMappings = () => {
    return !destinations.find(d => !taskData.find(t => t.uuid == d.taskId));
  }

  const beginEditing = () => {

    //Unmark the drawers. This serves two purposes:
    //  1. Prevents drawers opening due to badge swipes while editing, and
    //  2. If the nurse indeed changes the batch, prevents the drawers 
    //     from being marked at the get-go of the replacement batch.
    for(let i = 0; i < destinations.length; ++i) {
      if(destinations[i].drawer)
        toggleStorageSelection(destinations[i].drawer, undefined);
    }

    let restricted = false; //derive this from orig tasks
    let codeRequired = false;
    let test = false;
    let origBatch = destinations.map((d, i) => {

      let task = taskData.find(t => t.uuid == d.taskId);  //guaranteed by verifyDestMappings

      let {start_goal, end_goal} = task.task_level_args;

      if(task.task_data.restricted)
        restricted = true;  //overall batch

      if(task.task_data.code_required)
        codeRequired = true;  //note: codes are not generated until after drawers are loaded, so we're safe to edit

      if(task.task_data.test)
        test = true;

      return {
        id:i,
        startGoal:r2h(start_goal.value),
        endGoal:r2h(end_goal.value),
        note:d.note || ''   //note:task.task_data.sender_note || ''
      };
    });

    let m1FriendlyPickup = origBatch[0].startGoal;
    let endGoals = getEnabledKioskFriendlyNames();
    endGoals = endGoals.filter(friendlyName => friendlyName !== m1FriendlyPickup);
    endGoals.sort();

    let editedBatch = structuredClone(origBatch);

    // elasticLogEvent({what:'batchSnapshot', msg:'snapshot of data at beginEditing', origBatch});

    setOnBotEditCtx({endGoals, origBatch, editedBatch, restricted, origRestricted:restricted, codeRequired, test});
  }

  const clickCancelAndUnload = () => {
    let drawersToUnload = getDrawersLoadedForCurrentTask();
    
    // the cases are split like this so that we can run func clickCancel() again when the drawers are unloaded
    if(drawersToUnload.length > 0){
      onClickCancelPendingUnload();
      setShowUnloadDialog(true);
    }
    else {
      clickCancel();
    }
  }

  const scheduled = destinations.find(d => {
    let task = taskData.find(t => t.uuid == d.taskId);
    return !!task && !!task.task_data?.scheduled;
  });
  const showLoadEdits = !!autoPrioConfig?.enableLoadEdits && !scheduled;

  let width = Math.round((100 - 15) / (showLoadEdits ? 3 : 2)) + '%';

  const loadButtonSettings = [];

  loadButtonSettings.push({
    phrase: showCancelRatherThanGoBack() ? 'CANCEL' : 'GO BACK',
    borderColor: cancelColor,
    fillColor: 'transparent',
    fontColor: cancelColor,
    action: clickGoBack,
    disabled: phase === Phase.Load,
    width
  });

  if(showLoadEdits) {

    loadButtonSettings.push({
      phrase: 'CHANGE STOPS',
      fillColor: confirmColor,
      borderColor: confirmBorderColor,
      fontColor: 'white',
      action: () => beginEditing(),
      disabled:verifyDestMappings() && !drawersAllowStopChanges(),
      width,
      buttonStyling:{fontSize:30}
    });
  }

  loadButtonSettings.push({
    phrase: continueButtonText || 'DELIVER',
    fillColor: confirmColor,
    borderColor: confirmBorderColor,
    fontColor: 'white',
    action: clickDeliver,
    disabled: isDeliverDisabled(),
    width
  });



  if(onBotEditCtx) {

    return <RequestScreen
      disableStartGoalSelection={true}
      endGoals={onBotEditCtx.endGoals}
      defaultStartGoal={onBotEditCtx.origBatch[0].startGoal}
      closeModal={(params?: ModalParams) => setOnBotEditCtx(undefined)}
      clickRequest={() => setConfirmVisible(true)}
      dropoffs={onBotEditCtx.editedBatch}
      setDropoffs={(editedBatch: Dropoff[]) => setOnBotEditCtx({...onBotEditCtx, editedBatch})}
      isRestricted={!!onBotEditCtx.restricted} 
      setRestricted={(value: boolean) => setOnBotEditCtx({...onBotEditCtx, restricted: value, codeRequired: !value ? false : onBotEditCtx.codeRequired})}
      isCodeRequired={!!onBotEditCtx.codeRequired}
      setPinRequired={(value: boolean) => setOnBotEditCtx({...onBotEditCtx, codeRequired: value})}
      showRestricted={true} //this.state.showRestricted
      noKioskText={`Please call Moxi support`}  //this.noKioskText()
      getOpenHoursInfo={(stationCode:string) => getOpenHoursInfo(stationCode)}
      onBotEditCtx={onBotEditCtx}
      autoPrioConfig={autoPrioConfig}
      drawersAllowStopChanges={drawersAllowStopChanges()}
      isTest={onBotEditCtx.test}
    />
  }

  const enableLoadCancel = !!autoPrioConfig?.enableLoadCancel;
  let cancelFunc = enableLoadCancel ? clickCancelAndUnload : clickCancel;
  const cancelText = `Moxi support ${
    supportNumber ? `(${supportNumber}) ` : ''
  }could help resolve an issue without canceling the task. Are you sure you want to cancel this task?`;

  if(showUnloadDialog && enableLoadCancel){
    return (
      <LoadCancelScreen
        tapToReload={() => countForReload({ label: 'LoadCancel' })}
        lastScanTimestamp={storageState.lastScanTimestamp}
        scanResult={storageState.scanResult}
        drawerStatus={storageState.status}
        clickCancel={clickCancel}
        readyToCancel={storageState.readyToCancel}
        taskUuid={taskUuid}
      />
    );
  }

  return (
    <>
      {showCancelDialog && (
        <Dialog
          title="Cancel This Batch?"
          text={cancelText}
          primaryButtonText="CANCEL TASK"
          primaryButtonColor={cancelColor}
          clickPrimaryButton={cancelFunc}
          secondaryButtonText="GO BACK"
          secondaryButtonColor={confirmBorderColor}
          clickSecondaryButton={() => setShowCancelDialog(false)}
        />
      )}
      <div className="batch-load-items-screen">
        <CornerGripperLogo tapToReload={tapToReload} />
        <div className="batch-load-items-screen__header">
          <div className="header__title-wrapper">{getTitle()}</div>
          {requestedBy && (
            <div className="header__requested-by-wrapper">
              <span className="header__requested-by-text">Requested By </span>
              <span className="header__requested-by-text--emph">{requestedBy}</span>
            </div>
          )}
        </div>
        <div className="batch-load-items-screen__content">
          {phase === Phase.Scan || drawersRequestedAndFilled() ? (
            <div className="content__badge-reader">
              <img draggable="false" src={require('../assets/images/moxi-badge-reader.gif')} alt="Moxi badge scan" />
            </div>
          ) : (
            <StoragePlusLoadingView
              locktubes={storageState}
              toggleTubeSelected={handleDrawerToggle}
              disabled={phase === Phase.Ready}
            />
          )}
          <div className="content__note-wrapper">
            {isRestricted && (
              <div className="note-wrapper__header">{codeRequired ? 'Multi-factor' : 'Restricted'} Delivery to</div>
            )}
            <div className="note-wrapper">
              {destinations.map((destination, index) => (
                <div className="dropoff" key={`dropoff-${destination.id}`}>
                  <div className="dropoff__header" ref={(el: HTMLDivElement) => (inputsRef.current[index] = el)}>
                    <div
                      className={`dropoff__header-name${
                        phase === Phase.Select && destinations[getDestSansDrawerIdx()]?.name == destination.name
                          ? ' dropoff__header-name--emph'
                          : ''
                      }`}
                    >
                      {r2h(destination.name)}
                    </div>
                    {destination.drawer && (
                      <div
                        className={`drawer-button${
                          isDrawerOpen(destination.drawer)
                            ? ' drawer-button--open'
                            : phase === Phase.Ready || isDrawerReady(destination.drawer)
                            ? ' drawer-button--filled'
                            : ''
                        }${isOnetoTwoBatchLoaded() ? ' drawer-button--empty' : ''}`}
                      >
                        {destination.drawer}
                        {!isOnetoTwoBatchLoaded() && (
                          <button onClick={() => {noteInteraction(); toggleStorageSelection(destination.drawer, null);}}>
                            <FontAwesomeIcon icon={faXmark} />
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                  <textarea
                    className="content__note-input"
                    onClick={() => {
                      noteInteraction();
                      setTimeout(
                        () =>
                          inputsRef.current &&
                          inputsRef.current[index] &&
                          inputsRef.current[index].scrollIntoView({ behavior: 'smooth' }),
                        150,
                      );
                    }}
                    onChange={(e) => handleNoteChange(destination.id, e.target.value)}
                    placeholder="Add a note"
                    value={destination.note}
                    rows={destinations.length < 3 ? 3 : 1}
                  />
                </div>
              ))}
            </div>
          </div>
        </div>
        <div className="batch-load-items-screen__footer">
          <HorizontalButtonPairView buttonSettings={loadButtonSettings} />
        </div>
      </div>
    </>
  );
}
